import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  TextField,
  Box,
  Alert,
  TablePagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import { useDispatch } from "react-redux";
import { markAsRead, markAllAsRead } from "../store/slices/notificationSlice";
import { useAppSelector } from "../store";
import { getNotifications } from "../store/slices/notificationSlice";
import { getAuthState } from "../store/slices/authSlice";
import { useNavigate } from "react-router-dom";
import Api from "../services/apis/setup";
import NotificationsTable from "../components/NotificationsTable";

function NotificationsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const notifications = useAppSelector(getNotifications);
  const { user } = useAppSelector(getAuthState);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tableNotifications, setTableNotifications] = useState<any[]>([]);
  const [fetchNotifications] = Api.useLazyFetchNotificationsQuery();
  const [markNotificationRead] = Api.useMarkNotificationReadMutation();

  // Fetch notifications with pagination from server
  useEffect(() => {
    if (user) {
      loadNotifications();
    }
  }, [user, page, rowsPerPage]);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  const loadNotifications = async () => {
    setLoading(true);
    try {
      const result: any = await fetchNotifications({
        baseURL: "/vehicles/notifications",
        params: {
          page: page + 1,
          limit: rowsPerPage
        }
      });

      const response = result?.data;
      if (response?.data) {
        // Store in local state for table
        setTableNotifications(response.data);

        // Set total from pagination
        if (response.pagination?.total) {
          setTotalNotifications(response.pagination.total);
        }
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter notifications based on search term (local filtering)
  const filteredNotifications = tableNotifications
    .filter(({ title = '', message = '' }) => {
      const searchTermLower = searchTerm.toLowerCase();
      return (
        title.toLowerCase().includes(searchTermLower) ||
        message.toLowerCase().includes(searchTermLower)
      );
    })
    .filter((notification) => {
      // Always show only unread notifications
      return !notification.read;
    });

  const handleChangePage = (event: unknown, newPage: number) => {
    console.log('Page changed to:', newPage);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMarkAsRead = async (notificationId: string) => {
    // Update local state
    dispatch(markAsRead(notificationId));
    // Persist to database
    try {
      await markNotificationRead({
        baseURL: `/vehicles/notifications/${notificationId}/read`,
        method: 'PATCH',
        payload: {}
      });
      // Reload
      loadNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = async (notification: any) => {
    // Mark as read before navigating
    if (notification.id && !notification.read) {
      dispatch(markAsRead(notification.id));
      try {
        await markNotificationRead({
          baseURL: `/vehicles/notifications/${notification.id}/read`,
          method: 'PATCH',
          payload: {}
        });
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
    // Navigate to service history details page
    if (notification.vehicleId) {
      navigate(`/service-history/${notification.vehicleId}`);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <NotificationsActiveIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Notifications
        </Typography>
        {totalNotifications > 0 && (
          <Box sx={{
            backgroundColor: 'primary.main',
            color: 'white',
            px: 2,
            py: 0.5,
            borderRadius: 2,
            fontSize: '0.875rem',
            fontWeight: 600
          }}>
            {totalNotifications} total
          </Box>
        )}
      </Box>

      {/* Search */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search notifications..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
          }}
        />
      </Box>

      {/* Notifications Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <Typography color="textSecondary">Loading notifications...</Typography>
        </Box>
      ) : filteredNotifications.length === 0 ? (
        <Alert severity="info">
          {searchTerm
            ? 'No notifications match your search.'
            : 'You\'re all caught up! No unread notifications.'}
        </Alert>
      ) : (
        <Box sx={{ mb: 3 }}>
          <NotificationsTable
            notifications={filteredNotifications}
            onMarkAsRead={handleMarkAsRead}
            onNotificationClick={handleNotificationClick}
          />
        </Box>
      )}

      {/* Pagination */}
      {totalNotifications > 0 && (
        <TablePagination
          component="div"
          count={totalNotifications}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      )}
    </Container>
  );
}

export default NotificationsPage;
